import { ReactElement, ReactNode, RefObject, SVGAttributes } from 'react';
import * as Immutable from 'immutable';
import Contact from './models/Contact';
import SearchResults from './models/SearchResults';
import { Emitter } from 'mitt';
import { Location } from 'react-router-dom';
import { DeviceIntegrationProvider } from './models/Integration';
import Group from './models/Group';
import SystemAlert from './models/SystemAlert';
import Note from './models/Note';
import { ActivityItem } from './models/Activity';
import type { AuthPlatform } from './helpers/jwt';
import Duplicate from './models/Duplicate';

export type ID = string;
export interface IDRecord {
  id: ID;
}

export type ClientEnv = 'Web' | 'Mac' | 'Windows' | 'iOS';

export interface ReduxAction {
  type: string;
  payload?: any;
  error?: boolean;
}

interface CustomSVGProps {
  title?: string;
  color?: 'primary' | 'textPrimary' | 'textSecondary';
  customColor?: string;
  hover?: boolean;
  // if true, we lighten the icon by default with opacity, then go to full opacity on hover
  hoverOpacity?: boolean;
  active?: boolean;
  forwardedRef?: RefObject<SVGSVGElement>;
}

export type SVGProps = CustomSVGProps & SVGAttributes<SVGElement>;

export interface UserAction {
  title: ReactNode;
  type: string;
  action: (args?: any) => void;
  icon?: (props: SVGProps) => ReactElement<any, any>;
  inlineIcon?: (props: SVGProps) => ReactElement<any, any>;
  disabled?: boolean;
  synonyms?: Array<string>;
  keyboard?: string;
  className?: string;
  subtitle?: ReactNode;
}

// Search types
export type SearchType =
  | 'search'
  | 'adHocSearch'
  | 'mentionSearch'
  | 'allPeopleSearch'
  | 'groupSearch'
  | 'archiveSearch';

export type TipKey =
  | 'interest'
  | 'org'
  | 'location'
  | 'social_media'
  | 'date'
  | 'relationship'
  | 'education'
  | 'content_type'
  | 'upcoming_birthdays'
  | 'cleanups'
  | 'all';

export type SearchSortBy =
  | 'closeness'
  | 'firstName'
  | 'lastName'
  | 'firstInteraction'
  | 'created'
  | 'lastInteraction'
  | 'archive_date';

export type SearchSortByField =
  | 'firstName'
  | 'lastName'
  | 'firstInteractionDate'
  | 'lastInteractionDate'
  | 'created'
  | 'score'
  | 'archive_date';

export type SearchSortDirection = 'asc' | 'desc';

export interface SearchSort {
  name?: SearchSortBy;
  direction?: SearchSortDirection;
}

export interface SearchState {
  type: SearchType;
  term: string;
  termForResults: string;
  results: SearchResults;
  sorting: SearchSort;
  recent?: Array<any>;
  tips?: { [K in TipKey]: Array<string> };
  deletedIDCacheToExpire: Immutable.Set<ID>;
  resourceID?: ID;
  pauseLoadingMore?: boolean;
}

// Type for building UI around available user actions given the current app state
export interface UserActionState {
  selectedContact?: Contact;
  selectedContactBatch?: Immutable.Set<ID>;
  selectedContactArray?: Array<Contact>;
  groupView?: GroupViewState & { isShared: boolean };
  archiveView?: ArchiveViewState;
  duplicatesView?: DuplicatesViewState;
  isMultiselectView?: boolean;
  location?: Location;
}

type MenuOption = Partial<UserAction> & {
  divider?: boolean;
  suffix?: ReactNode;
  keepMenuOpenOnClick?: boolean;
  active?: boolean;
  button?: ReactNode;
  focusedClassName?: string;
};

export type DropdownMenuOption = MenuOption & {
  submenu?: MenuOption[];
};

export interface ClayGlobalRelationshipsData {
  startCount: number;
  currentHighCount: number;
  incrementStartTime: number; // unix timestamp
}

export type ProductName = 'Clay Personal' | 'Clay Pro' | 'Clay Teams';

export interface IAPProduct {
  productIdentifier: string;
  formattedPrice: string;
  price: number;
  subscriptionPeriod: {
    numberOfUnits: number;
    unit: 'month' | 'year';
  };
  // ...other unused properties
}

export type IAPTransactionState =
  | 'purchasing'
  | 'purchased'
  | 'failed'
  | 'restored'
  | 'deferred'
  | 'finished';

export interface IAPTransactionEvent {
  transactionState: IAPTransactionState;
  errorMessage?: string;
  transactionIdentifier?: string;
  originalTransactionIdentifier?: string;
  payment: {
    productIdentifier: 'com.clay.membership.annual' | 'com.clay.membership.monthly';
  };
}

interface Entitlements {
  'contact.limit': number | null;
  'integrations.email': boolean;
  'search.internal': boolean;
  'nexus.enabled': boolean;
  'nexus.beta': boolean;
  'account.delete': boolean;
  'account.team_member': boolean;
  'additional.team.members': number;
  'search.full': boolean;
  'permissions.contractor': boolean;
  'permissions.admin': boolean;
  'permissions.staff': boolean;
  'linkedin.version': number;
  'groups.export': boolean;
  'contacts.edit.disabled': boolean;
}

export type EntitlementName = keyof Entitlements;

export type UserEntitlements = Partial<Entitlements>;

export const ViewContactPermission = 'view_contact';
export type ContactSubPermission =
  | 'view_contact_notes'
  | 'view_contact_moments'
  | 'view_contact_contactinfo';
export type ContactPermission = typeof ViewContactPermission | ContactSubPermission;

export const ViewGroupPermission = 'view_list';
export type GroupSubPermission = 'view_list_notes' | 'view_list_moments' | 'view_list_contactinfo';
export type GroupPermission = typeof ViewGroupPermission | GroupSubPermission;

export type AnalyticsConnectionType =
  | 'Google'
  | 'Microsoft'
  | 'Twitter'
  | 'Linkedin'
  | 'Contacts'
  | 'iMessage'
  | 'WhatsApp'
  | 'Instagram'
  | 'Phone';

export type BillingProvider = 'Stripe' | 'AppSumo' | 'RevenueCat';
type SubscriptionStatus = 'active' | 'cancelled' | 'inactive';

export interface ClaySubscription {
  id: number;
  provider_id: string; // this is the Stripe ID for the subscription
  plan: {
    name: string;
    provider: BillingProvider;
    product: { name: ProductName };
  };
  status: SubscriptionStatus;
  deactivated: string | null;
  created: string;
  updated: string;
  trial: boolean;
  management_url: string;
}

export interface AuthAccountData {
  display_name: string | null;
  email: string | null;
  id: number;
  provider: AuthPlatform;
  remote_id: string | null;
  scope: {
    id: number;
    scope: string;
  } | null;
  username: string | null;
  date: string;
}

export interface UserSession {
  primary_product: { name: ProductName };
  id: number;
  assistant_id?: number;
  algolia_restricted_search_key: string;
  avatar_options: Array<string>;
  avatar_url: string;
  typesense_search_key: string;
  contact_id: number;
  email: string;
  first_name: string;
  last_name: string;
  social_linkedin_url: string;
  social_twitter_url: string;
  username: string;
  zapier_key: string;
  has_completed_onboarding: boolean;
  upgrade_url?: string;
  // undefined means we're missing our entitlement (should not happen), null means we have no limit
  contactLimit?: number | null;
  isOverContactLimit?: boolean;
  billing_provider: BillingProvider;
  churnkey: string;
  stripe_customer_id: string;
}

export type UserProfileRequest = Partial<
  Omit<UserSession, 'avatar_url'> & { avatar_url: string | File }
>;

interface AccountsProgress {
  total: number;
  importing: number;
  processed: number;
}

interface EnrichmentProgress {
  total: number;
  processed: number;
}

export interface UserAccountProcessing {
  status: string;
  accounts: AccountsProgress;
  enrichment: EnrichmentProgress;
}

export type AlertType = 'loading' | 'success' | 'error' | 'system';

export type SystemAlertSeverity = 'low' | 'medium' | 'high';

export type ButtonVariant =
  | 'none'
  | 'flex'
  | 'small'
  | 'muted'
  | 'primary'
  | 'warning'
  | 'danger'
  | 'icon'
  | 'block'
  // NEW BUTTONS!
  | 'v2';

export interface SelectOption {
  label: string;
  value: string | number;
}

// In-app confirmation dialog types
export type ConfirmButtonConfig = React.HTMLProps<HTMLButtonElement> & {
  title: string;
  value: any;
  variant?: ButtonVariant;
};

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  action?: string;
  actions?: Array<ConfirmButtonConfig>;
  variant?: ButtonVariant;
  smallModal?: boolean;
  noCancelButton?: boolean;
  forceDarkMode?: boolean;
}

export type ConfirmCallback = (callbackValue: any) => void;

export type Confirm = (opts: ConfirmOptions, cb: ConfirmCallback) => void;

export type ContactInfoSource = 'BI' | 'CL' | 'EM' | 'LI' | 'MA' | 'ME' | 'SS' | 'TW' | 'FB' | 'IG';
export type ContactInfoType =
  | 'email'
  | 'linkedin'
  | 'manual'
  | 'phone'
  | 'messages'
  | 'twitter'
  | 'url'
  | 'facebook'
  | 'instagram';

export interface ContactInfo {
  value: string;
  type?: ContactInfoType;
  source?: ContactInfoSource;
  label?: string;
}

export interface Coords {
  lat: number;
  lon: number;
}

export interface IntegrationState {
  enabled?: boolean;
  isSupported?: boolean;
  sync?: () => void;
  disconnect?: (action: 'delete' | 'archive') => Promise<void>;
  setup?: () => void;
}

export type GroupActionSource =
  | 'Search Menu'
  | 'Search Command Bar'
  | 'List'
  | 'Merge Modal'
  | 'Keyboard Shortcut'
  | 'Duplicates View';

export interface CustomAppProps {
  confirm: Confirm;
  isElectron?: boolean;
}

export type AppSessionEvent = {
  'hotkey': KeyboardEvent;
  'hotkey_1': KeyboardEvent;
  'hotkey_2': KeyboardEvent;
  'keydown': KeyboardEvent;
  'keydown:all': KeyboardEvent;
  'shortcuts:open': void;
  'window:focus': FocusEvent;
};

export type AppEmitter = Emitter<AppSessionEvent>;
export type AppSession = AppEmitter & CustomAppProps;

export enum DesktopUpdateStatus {
  checking = 'checking',
  downloading = 'downloading',
  unavailable = 'unavailable',
  ready = 'ready',
}

export enum DesktopTriggeredUIAction {
  showNewPersonView = 'show-new-person-view',
  showNewNoteView = 'show-new-note-view',
  toggleSidebar = 'toggle-sidebar',
  toggleNotesView = 'toggle-notes-view',
  showSearchView = 'show-search-view',
  showHomeView = 'show-home-view',
  showPeopleView = 'show-people-view',
  showNotesView = 'show-notes-view',
  showStarredView = 'show-starred-view',
  showNexusView = 'show-nexus-view',
  showKeyboardShortcuts = 'show-keyboard-shortcuts',
  showSettingsView = 'show-settings-view',
  showCommandBar = 'show-command-bar',
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      added: { added: string };
      removed: { removed: string };
      contacts: { contacts: string };
      events: { events: string };
      notes: { notes: string };
    }
  }
}

interface LinkedInEducationExperience {
  school: string;
  degree?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
}

interface LinkedInWorkExperience {
  company: string;
  role: string;
  startDate: string;
  endDate?: string;
  duration: string;
  description?: string;
}

export interface LinkedInProfile {
  username: string;
  firstName: string;
  lastName: string;
  headline: string;
  avatarUrl: string;
  location: string;
  country: string;
  bio: string;
  education: LinkedInEducationExperience[];
  experience: LinkedInWorkExperience[];
}

/**
 * Events passed via preload.js
 */
export enum IntegrationRequestEvent {
  requestSyncState = 'requestSyncState',
  requestSync = 'requestSync',
  isAuthorized = 'isAuthorized',
  checkDiskAccess = 'checkDiskAccess',
  requestAuthorization = 'requestAuthorization',
  requestDiskAccess = 'requestDiskAccess',
  remove = 'remove',
}

export type SupportedIntegration = {
  platform: DeviceIntegrationProvider;
  requiresAuth?: boolean;
};

export enum DesktopIntegrationStatusEventType {
  startingSync = 'startingSync',
  completedSync = 'completedSync',
  progress = 'progress',
  auth = 'auth',
  removed = 'removed',
  error = 'error',
  aborted = 'aborted',
}

export type DesktopIntegrationStatusEvent =
  | {
      id?: string;
      status: DesktopIntegrationStatusEventType.startingSync;
    }
  | {
      id?: string;
      status: DesktopIntegrationStatusEventType.completedSync;
      total: number;
    }
  | {
      id?: string;
      status: DesktopIntegrationStatusEventType.progress;
      complete: number;
      total: number;
    }
  | {
      id?: string;
      status: DesktopIntegrationStatusEventType.auth;
      authed: boolean;
    }
  | {
      id?: string;
      status: DesktopIntegrationStatusEventType.removed;
    }
  | {
      id?: string;
      status: DesktopIntegrationStatusEventType.aborted;
    }
  | {
      id?: string;
      status: DesktopIntegrationStatusEventType.error;
      error?: string;
      type?: string;
    };

export enum DesktopProfileUpdateStatusEventType {
  profileUpdateError = 'profileUpdateError',
  profileUpdateCompleted = 'profileUpdateCompleted',
  linkedInNeedsAuth = 'linkedInNeedsAuth',
}

export type DesktopProfileUpdateStatusEvent =
  | {
      contactID: ID;
      status: DesktopProfileUpdateStatusEventType.profileUpdateError;
      error: string;
    }
  | {
      contactID: ID;
      status: DesktopProfileUpdateStatusEventType.profileUpdateCompleted;
      profile: LinkedInProfile;
      isPartial: boolean;
    }
  | {
      contactID: ID;
      status: DesktopProfileUpdateStatusEventType.linkedInNeedsAuth;
    };

export type IntegrationStatusEventCallback = (
  event: DesktopIntegrationStatusEvent | DesktopProfileUpdateStatusEvent,
) => void;

export type UnsubscribeFunction = () => void;

/***
 * Redux - UI slice types
 ***/
export interface AlertsState {
  message: any;
  systemAlert: SystemAlert | null;
  lastDismissedSystemAlert: string | null;
}

export interface ArchiveViewState {
  selectedContact: Contact | null;
  selectedContactBatch: Immutable.Set<ID>;
}

export interface ContactViewState {
  creatingNewNote: boolean;
  newReminderEditorOpen?: boolean;
}

export interface DuplicatesViewState {
  selectedDuplicateID: ID | null;
  selectedDuplicate?: Duplicate;
}

export type ContextMenuTarget =
  | 'Contact'
  | 'Referral'
  | 'ShareItem'
  | 'ArchivedContact'
  | 'Duplicate';

export interface ContextMenuState {
  isOpen: boolean;
  target?: ContextMenuTarget;
  coords: { x: number; y: number };
  viewportDimensions: { width: number; height: number };
  params?: Record<string, any>;
}

export interface GroupViewState {
  group: Group | null;
  initialGroupState: Group | null;
  isEditing: boolean;
  isEditingContacts: boolean;
}

export type AppView =
  | 'Home'
  | 'Review'
  | 'People'
  | 'Notes'
  | 'Reminders'
  | 'Search'
  | 'Settings'
  | 'Contact - Detail'
  | 'Contact - Edit'
  | 'Group - Detail'
  | 'Group - Create'
  | 'Tour'
  | 'no view matched';

export interface LocationState {
  lastPathname?: string;
  pathname?: string;
  view?: AppView;
  pageSource?: string | undefined;
  redirect: string | null;
}

export type ModalType =
  | 'expandedActivityItem'
  | 'addToGroups'
  | 'commandBar'
  | 'contactAndNote'
  | 'contactPerson'
  | 'contactSearch'
  | 'groupReconnect'
  | 'iAPUpgrade'
  | 'merge'
  | 'reconnect'
  | 'requestMissingBirthdays'
  | 'searchEducation'
  | 'settings'
  | 'shareContact'
  | 'shareGroup'
  | 'shareInvite'
  | 'support';

export interface ModalState {
  isOpen: boolean;
  modalType?: ModalType;
  // describes from where and how the current state was derived
  dispatchOrigin?: string;
  contactAndNoteModal: {
    contact: Contact;
    note: Note;
    canEditContact: boolean;
    focusMode?: boolean;
    creatingNewReminder?: boolean;
  };
  newNoteDraft?: Note;
  settingsNav?: SettingsNavState;
  activityItemID?: ID;
  groupID?: ID;
}

export type SettingsPanel =
  | 'general'
  | 'profile'
  | 'home'
  | 'integrations'
  | 'notifications'
  | 'appearance'
  | 'about'
  | 'staging'
  | 'workspace-general'
  | 'workspace-members';

export type IntegrationsSubPanel = 'card-dav' | 'zapier' | 'make';
export type SettingsSubPanel = IntegrationsSubPanel;

export interface SettingsNavState {
  panel: SettingsPanel;
  subPanel?: SettingsSubPanel;
}

export type NavBarStatus = 'pinned' | 'hidden' | 'floating';

export interface NavBarState {
  status: NavBarStatus;
  transitioning?: boolean;
  exiting?: NavBarStatus;
  entering?: NavBarStatus;
}

type ScrollableViewID = string;
type ScrollPosition = number;
export type ScrollPositionState = Immutable.Map<ScrollableViewID, ScrollPosition>;

// This is a generic view state for saving a single selected contact state, e.g. in People view
type PersonListViewState = {
  selectedContact: Contact | null;
  selectedContactBatch: Immutable.Set<ID>;
};
type SearchViewState = PersonListViewState & { searchTerm: string };
export interface ActivityViewState {
  activeActivityItem?: ActivityItem;
  activeNested: boolean;
  nestedItem?: Contact;
}
type NotesListViewState = { highlightedNote: Note | null };
export type MapViewState = {
  selectedContactID: ID | null;
  selectedClusterID: number | null;
  selectedClusterContactIDs: ID[];
  selectedClusterSelectedContactID: ID | null;
  visibleMapRect: mapkit.MapRect | null;
};
export type RestorableViewID =
  | 'Search'
  | 'People'
  | 'Activity'
  | 'Notes'
  | `Group_${ID}`
  | `GroupActivity_${ID}`
  | 'Map';
export type ViewID = RestorableViewID | 'Archive' | 'Duplicates';
export type AnyViewState = Partial<
  SearchViewState & ActivityViewState & NotesListViewState & MapViewState
>;
export type ViewRestorationState = Immutable.Map<ViewID, AnyViewState>;

interface SavedContactSelection {
  pathname?: string;
  contact?: Contact;
  contactBatch?: Immutable.Set<ID> | null;
}

// We might consider moving the selected note (note currently being edited) into
// this slice of state. The "selected" contact is the currently highlighted contact.
export interface SelectedState {
  contact: Contact | null;
  contactEventSource: string | null;
  contactBatch: Immutable.Set<ID>;
  contactBatchEventSource: string | null;
  savedContactSelection: SavedContactSelection | null;
}

export interface PanelSizingState {
  navBarWidth: number;
  rightSidebarWidth: number;
}

export interface SourceLabelsState {
  email: Immutable.OrderedSet<string>;
  phone: Immutable.OrderedSet<string>;
}

export interface WelcomeGuideState {
  visible: boolean;
}

/***
 * Redux - User slice types
 ***/
export type UserMetadataFlag =
  | 'nexusCalloutDismissed'
  | 'requestBirthdaysCalloutDismissed'
  | 'accountProcessingToastDismissed'
  | 'forceShowImportingContactsAlert'
  | 'disableNoteReminderParsing'
  | 'showNotesInSidebar';

export type DefaultReminderTime = { hour: number; minute: number };

export type UserMetadata = Record<UserMetadataFlag, boolean> & {
  defaultReminderTime?: DefaultReminderTime;
};

export type NotificationType =
  | 'activity_birthdays'
  | 'activity_reconnects'
  | 'activity_group_reconnects'
  | 'activity_news'
  | 'activity_events'
  | 'activity_posts'
  | 'activity_diffs'
  | 'include_recent'
  | 'include_recurring_events'
  | 'pre_meeting'
  | 'in_meeting'
  | 'post_meeting'
  | 'clay_contact_user'
  | 'birthdays'
  | 'reconnects'
  | 'weekly_digest'
  | 'daily_brief'
  | 'include_people_without_information'
  | 'resolve_duplicates';
export type EnabledNotifications = Record<NotificationType, boolean>;

export type UserNotificationsState = {
  enabled: EnabledNotifications;
  updating: boolean;
};

interface PlatformAccountDetails {
  bio?: string;
  location?: string;
  link?: string;
}

export enum ProfileVisibility {
  Public = 'public',
  Private = 'private',
  Unknown = 'unknown',
}

export interface MinimalLinkedInProfile {
  id: string;
  urn: string;
  username: string;
  firstName?: string;
  lastName?: string;
  location?: string;
  country?: string;
  name?: string;
  avatarUrl?: string | null;
  headline?: string;
  details?: PlatformAccountDetails;
  visibility: ProfileVisibility;
}

type LinkedInProfileViewStatus = 'loading' | 'clayContact' | 'addToClay';

export interface LinkedInProfileViewState {
  status: LinkedInProfileViewStatus;
  contact?: Contact;
  profile?: MinimalLinkedInProfile;
  creatingContactFromProfile: boolean;
  mergingProfileIntoContact: boolean;
  refreshingContactData: boolean;
}

export type BXView =
  | 'minimizedTab'
  | 'login'
  | 'checkingAuth'
  | 'home'
  | 'contactProfile'
  | 'addPerson'
  | 'profileSearch'
  | 'linkedinProfile'
  | 'addingLinkedinProfile'
  | 'contactsAtOrg';

export interface BxUIState {
  minimized: boolean;
  view: BXView;
  viewSearchTerm: string;
  isViewSearchOpen: boolean;
  linkedinProfileSlug: string | undefined;
  parentHref: string | undefined;
  domain: string | undefined;
}
